import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { NotificationsService } from '../services/notifications.service';
import { getQueryParam, removeQueryParam } from '../misc/helper-functions.utils';
import { AuthService } from './auth.service';
import { WINDOW } from '../services/browser.tokens';

/**
 * @export
 * @class AuthGuard
 *
 * This service is used for authentication and cooperation with CAS
 */
@Injectable()
export class AuthGuard {
  private _authService = inject(AuthService);
  private _notificationsService = inject(NotificationsService);
  readonly windowRef = inject<Window>(WINDOW);

  authStatusSub$ = new ReplaySubject<boolean>();
  authStatusDone$ = this.authStatusSub$.pipe(take(1), filter(Boolean));

  tokenResolvedSub$ = new ReplaySubject<boolean>();
  tokenResolvedDone$ = this.tokenResolvedSub$.pipe(take(1), filter(Boolean));

  canActivate(): Observable<boolean> {
    const authToken = this._authService.getAuthToken();

    // remove ticket possible qp param
    const failureRedirectPath =
      this.windowRef.location.pathname + removeQueryParam(this.windowRef.location.search, 'ticket');

    // fresh application start in new window / tab
    // lets look to session storage
    if (!this._authService.isLoggedIn()) {
      return this._authService.retrieveAndSaveAuthToken().pipe(
        tap(() => this._authService.saveIsLoggedIn()),
        tap(() => this._passToApplication()),
        map(() => true),
        // TODO Overit proc se zde redirect se zpožděním 2s? Tento kod se vola i pri klasickem přihlášení
        catchError(() => this._delayedRedirect(failureRedirectPath))
      );
    }

    // valid token, not expired
    if (authToken && !this._authService.isAuthTokenExpired(authToken)) {
      return this._authService.user$.pipe(
        take(1),
        switchMap((user) => {
          if (user) {
            this._passToApplication();
            return of(false);
          }

          // uživatel není načten → načteme ho reaktivně
          return this._authService.fillUser().pipe(
            tap(() => this._passToApplication()),
            map(() => false)
          );
        })
      );
    }

    // expired token, we need to also find out the validity of CAS session
    // (catchError block hits, when e.g CAS session is expired)
    if (authToken && this._authService.isAuthTokenExpired(authToken)) {
      return this._authService.retrieveAndSaveAuthToken().pipe(
        tap(() => this._passToApplication()),
        map(() => true),
        catchError(() => {
          return this._delayedRedirect(failureRedirectPath);
        })
      );
    }

    // process auth flow with CAS entity
    const ticket = getQueryParam(this.windowRef.location.search, 'ticket');
    const currentCompleteUrl = encodeURIComponent(
      removeQueryParam(this.windowRef.location.origin + failureRedirectPath, 'ticket')
    );

    if (ticket)
      return this._authService.validateTicket(currentCompleteUrl, ticket).pipe(
        switchMap((res) => {
          if (!res.body?.includes('cas:authenticationSuccess')) {
            this._notificationsService.dispatchError('Autentizační služba selhala. Budete odhlášeni...');
            return this._delayedRedirect(failureRedirectPath);
          }
          return this._authService.retrieveAndSaveAuthToken();
        }),
        map((_) => {
          this._passToApplication();
          return true;
        }),
        catchError(() => {
          this._notificationsService.dispatchError('Autentizační služba selhala. Budete odhlášeni...');
          return this._delayedRedirect(failureRedirectPath);
        })
      );

    this._authService.redirectToLogin(failureRedirectPath);
    return of(false);
  }

  private _delayedRedirect(url: string) {
    return timer(2000).pipe(
      tap(() => {
        this._authService.removeAuthToken();
        this._authService.redirectToLogin(url);
      }),
      map(() => true)
    );
  }

  private _passToApplication() {
    this.authStatusSub$.next(true);
  }
}
